/**
 * Comments API client — wraps every comment endpoint from the spec.
 */

import { api } from '@/lib/api'

// ===================
// Types (mirrors backend repositories/comments.ts + comment-fix-actions.ts)
// ===================

export type CommentStatus = 'open' | 'resolved'
export type CommentCreatorRole = 'owner' | 'writer' | 'reviewer'

/**
 * What a comment thread hangs off.
 *
 * `draft` threads are anchored inside an artifact's prose (section + character
 * range); the extraction kinds — `requirement` (a compliance item), `risk` (a
 * risk-register item) and `criterion` (an evaluation criterion) — hang off a
 * whole row and carry no anchor at all. All four live in the same `comments`
 * table and are served by parallel route families, so every client-side call
 * has to say which.
 */
export type CommentTargetType = 'draft' | 'requirement' | 'risk' | 'criterion'

/**
 * The whole-object comment kinds — everything except `draft`. They share one
 * API surface and differ only in the route base they build from.
 */
export type ExtractionCommentTargetType = Exclude<CommentTargetType, 'draft'>

export interface SourceFindingRef {
  /**
   * Stable identity of the underlying issue (32 hex chars). Optional — comments
   * promoted before the issue model shipped only carry (runId, findingIndex).
   */
  issueKey?: string
  runId: string
  findingIndex: number
  criterion: string
  severity: 'high' | 'medium' | 'low'
}

/** `ai_fix` replies are machine-posted by the Resolve-with-AI lifecycle and immutable. */
export type CommentReplyKind = 'user' | 'ai_fix'

export interface CommentReply {
  id: string
  commentId: string
  body: string
  kind: CommentReplyKind
  createdBy: string
  createdByRole: CommentCreatorRole
  createdByName: string | null
  createdAt: string
  editedAt: string | null
  lastSeenByCounterpartyAt: string | null
}

export interface Comment {
  id: string
  organizationId: string
  bidId: string
  targetType: CommentTargetType
  targetId: string
  sectionId: string | null
  startPos: number | null
  endPos: number | null
  quotedText: string | null
  body: string
  status: CommentStatus
  sourceFindingRef: SourceFindingRef | null
  createdBy: string
  createdByRole: CommentCreatorRole
  createdByName: string | null
  createdAt: string
  editedAt: string | null
  resolvedBy: string | null
  resolvedAt: string | null
  postedAfterApproval: boolean
  preHandoffNote: boolean
  lastSeenByCounterpartyAt: string | null
  replies: CommentReply[]
}

export type CommentFixActionStatus = 'addressing' | 'applied' | 'dismissed' | 'failed'

export interface CommentFixAction {
  id: string
  commentId: string
  userId: string
  agentThreadId: string | null
  status: CommentFixActionStatus
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
}

export type CommentEffectiveRole = 'owner' | 'writer' | 'reviewer' | 'reader'

export interface CommentPermissions {
  effectiveRole: CommentEffectiveRole
  canCreate: boolean
  canReply: boolean
  canResolve: boolean
  canResolveWithAI: boolean
  canPromoteFromReview: boolean
  sectionCollaborationStatus: string | null
  artifactStatus: 'draft' | 'in_progress' | 'review' | 'finalized'
}

export interface CommentAnchor {
  sectionId: string | null
  startPos: number | null
  endPos: number | null
  quotedText: string | null
}

export interface CreateCommentInput {
  anchor: CommentAnchor
  body: string
  sourceFindingRef?: SourceFindingRef | null
  /**
   * Mentioned colleagues to add to the bid before the comment posts (the
   * composer's "Add … to this bid?" prompt). Draft comments only.
   */
  addToBid?: string[]
}

// ===================
// API
// ===================

/**
 * `watermark` is this viewer's last "opened the Comments tab" timestamp (null
 * if never) — the cut-line for unread. Listing is read-only and safe to poll:
 * it never advances the watermark and never stamps counterparty-seen.
 */
export function listComments(
  bidId: string,
  artifactId: string,
  status: 'open' | 'resolved' | 'all' = 'all',
): Promise<{
  comments: Comment[]
  fixActions: Record<string, CommentFixAction>
  watermark: string | null
}> {
  return api.get(
    `/bids/${bidId}/artifacts/${artifactId}/comments`,
    { status },
  )
}

/**
 * Announce that the viewer has actually opened the Comments tab. Advances the
 * unread watermark AND closes the counterparty's edit window — so call it on a
 * real view, never on a background poll.
 */
export function markCommentsViewed(
  bidId: string,
  artifactId: string,
): Promise<{ watermark: string }> {
  return api.post(`/bids/${bidId}/artifacts/${artifactId}/comments-viewed`)
}

export function getPermissions(
  bidId: string,
  artifactId: string,
  sectionId?: string | null,
): Promise<CommentPermissions> {
  const params: Record<string, string> = {}
  if (sectionId) params['sectionId'] = sectionId
  return api.get(
    `/bids/${bidId}/artifacts/${artifactId}/comment-permissions`,
    params,
  )
}

export function createComment(
  bidId: string,
  artifactId: string,
  input: CreateCommentInput,
): Promise<Comment> {
  return api.post(
    `/bids/${bidId}/artifacts/${artifactId}/comments`,
    input,
  )
}

export function updateComment(
  bidId: string,
  artifactId: string,
  commentId: string,
  body: string,
): Promise<Comment> {
  return api.patch(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}`,
    { body },
  )
}

export function deleteComment(
  bidId: string,
  artifactId: string,
  commentId: string,
): Promise<void> {
  return api.delete(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}`,
  )
}

export function resolveComment(
  bidId: string,
  artifactId: string,
  commentId: string,
): Promise<Comment> {
  return api.post(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/resolve`,
  )
}

export function reopenComment(
  bidId: string,
  artifactId: string,
  commentId: string,
): Promise<Comment> {
  return api.post(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/reopen`,
  )
}

export function addReply(
  bidId: string,
  artifactId: string,
  commentId: string,
  body: string,
  addToBid?: string[],
): Promise<CommentReply> {
  return api.post(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/replies`,
    addToBid && addToBid.length > 0 ? { body, addToBid } : { body },
  )
}

export function updateReply(
  bidId: string,
  artifactId: string,
  commentId: string,
  replyId: string,
  body: string,
): Promise<CommentReply> {
  return api.patch(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/replies/${replyId}`,
    { body },
  )
}

export function deleteReply(
  bidId: string,
  artifactId: string,
  commentId: string,
  replyId: string,
): Promise<void> {
  return api.delete(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/replies/${replyId}`,
  )
}

export function resolveWithAI(
  bidId: string,
  artifactId: string,
  commentId: string,
): Promise<{ threadId: string; actionId: string; threadSeed: string }> {
  return api.post(
    `/bids/${bidId}/artifacts/${artifactId}/comments/${commentId}/resolve-with-ai`,
  )
}

// ===================
// Requirement threads
// ===================

/**
 * Per-requirement thread counts for one bid, served in a single call so the
 * compliance table can badge every row without N round-trips. `watermark` is
 * the caller's own last-viewed time for that item — the unread cut-line.
 */
export interface RequirementCommentCounts {
  itemId: string
  open: number
  total: number
  lastActivityAt: string
  watermark: string | null
}

/**
 * Route base per extraction target kind. The families are byte-identical apart
 * from this segment, so the whole difference lives in one map rather than in a
 * branch inside every function below.
 */
const EXTRACTION_COMMENT_BASES = {
  requirement: 'compliance-items',
  risk: 'risk-items',
  criterion: 'evaluation-criteria',
} as const satisfies Record<ExtractionCommentTargetType, string>

const extractionCollection = (bidId: string, targetType: ExtractionCommentTargetType) =>
  `/bids/${bidId}/${EXTRACTION_COMMENT_BASES[targetType]}`

const requirementBase = (
  bidId: string,
  itemId: string,
  targetType: ExtractionCommentTargetType,
) => `${extractionCollection(bidId, targetType)}/${itemId}`

/**
 * Requirement threads have no fix-action lifecycle (Resolve-with-AI is
 * artifact-only), so this envelope carries no `fixActions` key at all — not an
 * empty one. Callers must default it themselves.
 */
export function fetchRequirementComments(
  bidId: string,
  itemId: string,
  status: 'open' | 'resolved' | 'all' = 'all',
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<{ comments: Comment[]; watermark: string | null }> {
  return api.get(`${requirementBase(bidId, itemId, targetType)}/comments`, { status })
}

export function markRequirementCommentsViewed(
  bidId: string,
  itemId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<{ watermark: string }> {
  return api.post(`${requirementBase(bidId, itemId, targetType)}/comments-viewed`)
}

export function fetchRequirementCommentPermissions(
  bidId: string,
  itemId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<CommentPermissions> {
  return api.get(`${requirementBase(bidId, itemId, targetType)}/comment-permissions`)
}

/**
 * Body only — the server 400s on any anchor field. A requirement thread is
 * about the whole requirement; there is no prose to point into.
 */
export function createRequirementComment(
  bidId: string,
  itemId: string,
  body: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<Comment> {
  return api.post(`${requirementBase(bidId, itemId, targetType)}/comments`, { body })
}

export function updateRequirementComment(
  bidId: string,
  itemId: string,
  commentId: string,
  body: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<Comment> {
  return api.patch(`${requirementBase(bidId, itemId, targetType)}/comments/${commentId}`, { body })
}

export function deleteRequirementComment(
  bidId: string,
  itemId: string,
  commentId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<void> {
  return api.delete(`${requirementBase(bidId, itemId, targetType)}/comments/${commentId}`)
}

export function resolveRequirementComment(
  bidId: string,
  itemId: string,
  commentId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<Comment> {
  return api.post(`${requirementBase(bidId, itemId, targetType)}/comments/${commentId}/resolve`)
}

export function reopenRequirementComment(
  bidId: string,
  itemId: string,
  commentId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<Comment> {
  return api.post(`${requirementBase(bidId, itemId, targetType)}/comments/${commentId}/reopen`)
}

export function addRequirementCommentReply(
  bidId: string,
  itemId: string,
  commentId: string,
  body: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<CommentReply> {
  return api.post(`${requirementBase(bidId, itemId, targetType)}/comments/${commentId}/replies`, {
    body,
  })
}

export function updateRequirementCommentReply(
  bidId: string,
  itemId: string,
  commentId: string,
  replyId: string,
  body: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<CommentReply> {
  return api.patch(
    `${requirementBase(bidId, itemId, targetType)}/comments/${commentId}/replies/${replyId}`,
    { body },
  )
}

export function deleteRequirementCommentReply(
  bidId: string,
  itemId: string,
  commentId: string,
  replyId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<void> {
  return api.delete(
    `${requirementBase(bidId, itemId, targetType)}/comments/${commentId}/replies/${replyId}`,
  )
}

export function fetchRequirementCommentCounts(
  bidId: string,
  targetType: ExtractionCommentTargetType = 'requirement',
): Promise<RequirementCommentCounts[]> {
  return api.get(`${extractionCollection(bidId, targetType)}/comment-counts`)
}
